import React, { useEffect, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { useGetWeatherAlertsRulesQuery } from "../store/slices/mapsSlice";
import {
  WeatherAlertConditions,
  WeatherForecastOptions,
} from "../WeatherAlerts/WeatherAlertConstants";
import {
  getAssetValueLabels,
  loadAssetOptionsPaginated,
  loadEventOptionsPaginated,
  proxyAPI,
} from "../proxyAPI";
import Subheading from "../Common/Subheading";
import AssetsAndContactsIcon from "../../assets/icons/Icon_Library/application icons/assets/events/assets_contacts-01.svg2";
import WeatherAlertsIcon from "../../assets/icons/Icon_Library/application icons/assets/weather.svg2";
import EventsIcon from "../../assets/icons/Icon_Library/application icons/assets/events.svg2";
import styled from "styled-components";
import MapLayerIcon from "../../assets/icons/Icon_Library/application icons/assets/events/map_layers-01.svg2";
import TimeLineIcon from "../../assets/icons/Icon_Library/application icons/general/calendar.svg2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesLeft,
  faAnglesRight,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import { useLocation } from "react-router-dom";
import MyFilterSubheading from "../Common/MyFilterSubheading";
import StyledTabContainer from "../Common/StyledTabContainer";
import { useGetEventsQuery } from "../store/slices/settingsSlice";
import HelpButton from "../HelpContent/HelpButton";

const alertPeriodOptions = {
  hourly: { label: "Hourly", value: "hourly", slices: 24 },
  "3hourly": { label: "3 Hourly", value: "3hourly", slices: 8 },
  daily: { label: "Daily", value: "daily", slices: 7 },
  weekly: { label: "Weekly", value: "weekly", slices: 4 },
  monthly: { label: "Monthly", value: "monthly", slices: 12 },
};

const tabs = [
  {
    name: "Assets & Contacts",
    heading: "Assets & Contacts",
    subheading: "View your Assets and Contacts.",
    icon: AssetsAndContactsIcon,
    value: "true|false|false",
    eventKey: "assets-contacts",
  },
  {
    name: "Weather Forecast",
    heading: "Weather Forecast",
    subheading: "View the Weather Forecast .",
    icon: WeatherAlertsIcon,
    value: "false|true|false",
    eventKey: "weather-forecast",
  },
  {
    name: "Events",
    heading: "Events",
    subheading: "View your Events.",
    icon: EventsIcon,
    disabled: true,
    value: "false|false|true",
    eventKey: "events",
  },
];

export type WeatherFiltersType = {
  isWeather: boolean;
  showGrids: boolean;
  StartAt: string;
  EndAt: string;
  range: number;
  period: keyof typeof alertPeriodOptions;
  alertFilters: {
    ruleID?: number;
    conditionIds: number[];
    conditions: string[];
    search: string;
  };
};

export type WeatherForecastFiltersType = {
  isForecast: boolean;
  start: string;
  radius: number;
  steps: number;
  date: string;
  conditions?: string[];
  showSlider?: boolean;
  mapZoomLevel?: number;
};

export type MapFilterType = {
  Contacts: boolean;
  // GetLocations: include assets associated with location
  Assets: boolean;
  // GetContacts: include contacts associated with location
  Direct?: boolean;
  // GetContacts: include contacts associated with assets matching the location
  Indirect?: boolean;
  // GetContacts: exclude indirect contacts if have location
  ExcludeWithLocation?: boolean;
  OnlyItemsWithEvents?: boolean;
  ContactUuids?: string[];
  AssetUuids?: string[];
  weatherFilters?: WeatherFiltersType;
  weatherForecastFilters?: WeatherForecastFiltersType;
  eventsFilters?: {
    isEvent: boolean;
    severityLevel: string;
    period: keyof typeof alertPeriodOptions;
    range: number;
  };
  Events?: boolean;
  EventStatus?: ("starting" | "active" | "resolved" | "closed")[];
  EventSeverity?: ("low" | "medium" | "high" | "critical")[];
  EventsUuids?: string[];
};

const defaultFilters = (tab: string): MapFilterType => ({
  Assets: tab === "assets-contacts" || tab === "events",
  Contacts: tab === "assets-contacts",
  Direct: true,
  Indirect: false,
  ExcludeWithLocation: false,
  OnlyItemsWithEvents: tab === "events",
  weatherFilters: {
    isWeather: false,
    showGrids: false,
    StartAt: "",
    EndAt: "",
    range: -1,
    period: "daily",
    alertFilters: {
      ruleID: undefined,
      conditionIds: [],
      conditions: [],
      search: "",
    },
  },
  weatherForecastFilters: {
    isForecast: tab === "weather-forecast",
    start: "",
    radius: 30,
    steps: 8,
    date: "",
    mapZoomLevel: 7,
  },
  Events: tab === "events",
  EventStatus: ["active"],
  EventSeverity: [],
  EventsUuids: [],

  // eventsFilters: {
  //   isEvent: tab === "events",
  //   severityLevel: "",
  //   period: "daily",
  //   range: -1,
  // },
});

export default function MapFilters({
  onChange,
  setOverlay,
  setLoading,
  weatherForecastDates,
  beforeTimeline,
  bottom,
}: {
  onChange: (newFilters: MapFilterType) => void;
  setOverlay: (overlay: any) => void;
  setLoading: (loading: boolean) => void;
  weatherForecastDates?: string[];
  beforeTimeline?: React.ReactNode;
  bottom?: React.ReactNode;
}) {
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(
    location.state?.tab ||
      (localStorage.getItem("selectedTab")
        ? localStorage.getItem("selectedTab")
        : "assets-contacts")
  );
  const [filters, setFilters] = useState<MapFilterType>(
    location.state?.mapFilters ||
      (localStorage.getItem("mapFilters")
        ? JSON.parse(localStorage.getItem("mapFilters") || "")
        : defaultFilters(selectedTab))
  );

  useEffect(() => {
    (async () => {
      if (!filters.weatherForecastFilters?.isForecast) return;
      const data = await proxyAPI({
        prefix: "logic/workflows/api",
        url: "user-preferences/weather_map_filters",
        method: "POST",
        body: {
          conditions: filters.weatherForecastFilters?.conditions || [],
          showSlider: filters.weatherForecastFilters?.showSlider || false,
        },
      });
    })();
  }, [
    filters.weatherForecastFilters?.conditions,
    filters.weatherForecastFilters?.showSlider,
  ]);

  useEffect(() => {
    (async () => {
      if (!filters.weatherForecastFilters?.isForecast) return;
      const data = await proxyAPI({
        prefix: "logic/workflows/api",
        url: "user-preferences/weather_map_filters",
        method: "GET",
      });
      if (data) {
        setFilters((p: any) => ({
          ...p,
          weatherForecastFilters: {
            ...p.weatherForecastFilters,
            conditions: data.conditions || [],
            showSlider: data.showSlider || false,
          },
        }));
      }
    })();
  }, [filters.weatherForecastFilters?.isForecast]);

  useEffect(() => {
    onChange(filters);
    localStorage.setItem("mapFilters", JSON.stringify(filters));
    localStorage.setItem("selectedTab", selectedTab);
  }, [filters]);

  const [search, setSearch] = useState("");
  useEffect(() => {
    const timetout = setTimeout(() => {
      setFilters((p: any) => ({
        ...p,
        weatherFilters: {
          ...p.weatherFilters,
          alertFilters: {
            ...p.weatherFilters?.alertFilters,
            search,
          },
        },
      }));
    }, 500);
    return () => {
      clearTimeout(timetout);
    };
  }, [search]);

  const { data: { alerts: alertsRules } = { alerts: [], total: 0 } } =
    useGetWeatherAlertsRulesQuery({
      ...filters,
      Limit: 100,
      Offset: 0,
      WithContacts: true,
      Zoom: 30,
    });

  useEffect(() => {
    setFilters((p: any) => ({
      ...p,
      weatherForecastFilters: {
        ...p.weatherForecastFilters,
        date: weatherForecastDates?.[0],
      },
    }));
  }, [weatherForecastDates]);

  useEffect(() => {
    if (location.state?.mapFilters) {
      setFilters(location.state?.mapFilters);
    }
    if (location.state?.tab) {
      setSelectedTab(location.state?.tab);
    }
  }, [location.state]);

  const events = useGetEventsQuery(
    { page: 0, perPage: 100, status: "active" },
    { skip: !filters.eventsFilters?.isEvent }
  );

  return (
    <div>
      <StyledTabContainer isBackgroundFixed id="maps-tabs-container">
        <Tab.Container
          id="maps-tabs"
          activeKey={selectedTab}
          onSelect={(v: any) => {
            setSelectedTab(v);
            if (localStorage.getItem("mapFilters" + v)) {
              setFilters(
                JSON.parse(localStorage.getItem("mapFilters" + v) || "")
              );
              return;
            }
            setFilters(defaultFilters(v));
          }}
        >
          <Nav variant="tabs" className="mb-n1">
            <div className="flex-grow-1 ms-3">
              <Subheading
                Icon={MapLayerIcon}
                heading="See your Mapping and control views"
                subheading="See your Mapping "
              />
            </div>
            {tabs.map((t, i) => (
              <Nav.Item
                key={t.eventKey}
                className=" d-flex justify-content-between"
              >
                <Nav.Link
                  eventKey={t.eventKey}
                  className="fw-bold d-flex justify-content-between rounded-top-4 border-0"
                  style={{ width: 150 }}
                >
                  <div>
                    <t.icon height={40} className=" ms-3 me-2" />
                  </div>
                  <div className=" mt-1">{t.heading}</div>
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Tab.Container>
      </StyledTabContainer>

      <div>
        <div
          className={`bg-colored-reverse bg-accross rounded-bottom-2 rounded-start-2 ${
            filters.eventsFilters?.isEvent || filters.weatherFilters?.isWeather
              ? ""
              : " rounded-end-2 "
          }`}
        >
          <button
            className="btn float-end text-white"
            onClick={() => {
              setFilters(defaultFilters(selectedTab));
            }}
          >
            <FontAwesomeIcon icon={faRotateLeft} />
          </button>
          <div className="text-white d-flex align-items-end p-3">
            <div className="border-end svg-white border-opacity-50 border-secondary ms-2 me-2 text-nowrap">
              {(() => {
                const selected = tabs.find((t) => t.eventKey === selectedTab);
                return (
                  <MyFilterSubheading
                    Icon={selected?.icon}
                    heading={selected?.heading}
                    subheading={selected?.subheading}
                    iconWidth={50}
                  />
                );
              })()}
            </div>

            {/* Filters */}
            <div className="flex-grow-1 row">
              {/* <div className="col-3">
                <MapLayers />
              </div> */}

              {selectedTab === "assets-contacts" && (
                <>
                  <div className="col-auto">
                    <label className="fw-bold text-white p-2">Show</label>

                    <Input
                      inputType="radio"
                      radioName="showAssetsOrContacts"
                      value={[filters.Assets + "|" + filters.Contacts]}
                      onChange={([v]: any) =>
                        setFilters({
                          ...filters,
                          Assets: v.split("|")[0] === "true",
                          Contacts: v.split("|")[1] === "true",
                        })
                      }
                      options={[
                        { label: "Assets", value: "true|false" },
                        { label: "Contacts", value: "false|true" },
                        { label: "Both", value: "true|true" },
                      ]}
                    />
                  </div>
                </>
              )}

              {selectedTab === "events" && (
                <div className="col-auto">
                  <label className="fw-bold  text-white p-2">Event type:</label>
                  <Input
                    inputType="radio"
                    radioName="eventsAssetsWeather"
                    value={
                      filters.Events
                        ? ["events"]
                        : filters.weatherFilters?.isWeather
                        ? ["weather"]
                        : filters.Assets
                        ? ["assets"]
                        : [""]
                    }
                    onChange={([v]: any) => {
                      setFilters((p: any) => ({
                        ...p,
                        weatherFilters: {
                          ...p.weatherFilters,
                          isWeather: v === "weather",
                        },
                        eventsFilters: {
                          ...p.eventsFilters,
                          isEvent: v === "assets",
                        },
                        OnlyItemsWithEvents: v === "assets",
                        Events: v === "events",
                        Assets: v !== "weather",
                      }));
                    }}
                    options={[
                      { label: "Weather Alert", value: "weather" },
                      { label: "Events", value: "events" },
                      { label: "Assets", value: "assets" },
                    ]}
                  />
                </div>
              )}

              {filters.Assets &&
                !filters.Contacts &&
                !filters.Events &&
                !filters.OnlyItemsWithEvents && (
                  <div className="col-3">
                    <label className="fw-bold text-white p-2">
                      Filter by Assets:
                    </label>
                    <Input
                      inputType="select"
                      isMulti
                      value={filters.AssetUuids || []}
                      onChange={(v: any) =>
                        setFilters({ ...filters, AssetUuids: v })
                      }
                      loadOptions={loadAssetOptionsPaginated as any}
                      getValueLabels={getAssetValueLabels as any}
                      options={undefined}
                    />
                  </div>
                )}

              {(filters.Events || filters.OnlyItemsWithEvents) && (
                <>
                  <div className="col-3">
                    <label className="fw-bold text-white p-2">
                      Filter by event status:
                    </label>
                    <Input
                      inputType="select"
                      isMulti
                      value={filters.EventStatus || []}
                      onChange={(v: any) =>
                        setFilters({ ...filters, EventStatus: v })
                      }
                      options={[
                        { value: "creating", label: "Creating" },
                        { value: "active", label: "Active" },
                        { value: "resolved", label: "Resolved" },
                        { value: "closed", label: "Closed" },
                        { value: "archived", label: "Archived" },
                      ]}
                    />
                  </div>
                  <div className="col-3">
                    <label className="fw-bold text-white p-2">
                      Filter by event severity:
                    </label>
                    <Input
                      inputType="select"
                      isMulti
                      value={filters.EventSeverity || []}
                      onChange={(v: any) =>
                        setFilters({ ...filters, EventSeverity: v })
                      }
                      options={[
                        { value: "low", label: "Low" },
                        { value: "medium", label: "Medium" },
                        { value: "high", label: "High" },
                        { value: "critical", label: "Critical" },
                      ]}
                    />
                  </div>
                  <div className="col">
                    <label className="fw-bold text-white p-2">
                      Filter by Events:
                    </label>
                    <Input
                      inputType="select"
                      isMulti
                      value={filters.EventsUuids || []}
                      onChange={(v: any) => {
                        setFilters({ ...filters, EventsUuids: v });
                      }}
                      loadOptions={
                        ((search: string, prevOptions: any) =>
                          loadEventOptionsPaginated(
                            search,
                            prevOptions,
                            filters.EventsUuids
                          )) as any
                      }
                      // getValueLabels={
                      //   (() =>
                      //     loadEventOptionsPaginated(
                      //       "",
                      //       [],
                      //       filters.EventsUuids
                      //     ).then((res) => res.options)) as any
                      // }
                      options={undefined}
                    />
                  </div>
                </>
              )}

              {filters.weatherFilters?.isWeather && (
                <>
                  <div className="col-3">
                    <label className="fw-bold text-white p-2">
                      Weather Alert Description :
                    </label>
                    <Input
                      placeholder="Search Weather Alert Description"
                      value={[search]}
                      onChange={([v]: string[]) => {
                        setSearch(v);
                      }}
                      options={undefined}
                    />
                  </div>
                  <div className="col-3">
                    <label className="fw-bold text-white p-2">
                      Weather Alerts:
                    </label>
                    <Input
                      inputType="select"
                      isClearable
                      placeholder="Select Weather Alert"
                      value={
                        filters?.weatherFilters?.alertFilters?.ruleID
                          ? [filters?.weatherFilters.alertFilters?.ruleID]
                          : []
                      }
                      onChange={([v]: number[]) => {
                        setFilters((p: any) => ({
                          ...p,
                          weatherFilters: {
                            ...p.weatherFilters,
                            alertFilters: {
                              ...p.weatherFilters.alertFilters,
                              ruleID: v,
                              conditionIds:
                                alertsRules
                                  ?.find((r) => r.ID === v)
                                  ?.Conditions.map((c) => c.ID) ?? [],
                            },
                          },
                        }));
                      }}
                      options={alertsRules?.map((r) => ({
                        value: r.ID,
                        label: r.Description,
                      }))}
                    />
                  </div>
                  {filters?.weatherFilters?.alertFilters?.ruleID && (
                    <div className="col-3">
                      <label className="fw-bold text-white p-2">
                        Weather Alert Conditions:
                      </label>
                      <Input
                        inputType="select"
                        isMulti
                        isClearable
                        placeholder="Select Weather Alert Conditions"
                        value={
                          filters?.weatherFilters?.alertFilters?.conditionIds ||
                          []
                        }
                        onChange={(v: number[]) => {
                          setFilters((p: any) => ({
                            ...p,
                            weatherFilters: {
                              ...p.weatherFilters,
                              alertFilters: {
                                ...p.weatherFilters.alertFilters,
                                conditionIds: v,
                              },
                            },
                          }));
                        }}
                        options={alertsRules
                          ?.find(
                            (r) =>
                              r.ID ===
                              filters?.weatherFilters?.alertFilters?.ruleID
                          )
                          ?.Conditions.map((c) => ({
                            value: c.ID,
                            label: c.Description,
                          }))}
                      />
                    </div>
                  )}
                  <div className="col">
                    <label className="fw-bold text-white p-2">
                      Weather Alert Condition Type:
                    </label>
                    <Input
                      inputType="select"
                      isMulti
                      isClearable
                      placeholder="Select Weather Alert Condition Types"
                      value={
                        filters?.weatherFilters?.alertFilters?.conditions || []
                      }
                      onChange={(v: string[]) => {
                        setFilters((p: any) => ({
                          ...p,
                          weatherFilters: {
                            ...p.weatherFilters,
                            alertFilters: {
                              ...p.weatherFilters.alertFilters,
                              conditions: v,
                            },
                          },
                        }));
                      }}
                      options={WeatherAlertConditions.map((c) => ({
                        value: c.key,
                        label: c.label,
                      }))}
                    />
                  </div>
                </>
              )}

              {filters.weatherForecastFilters?.isForecast && (
                <>
                  <div className="col-auto">
                    <label className="fw-bold text-white p-2">
                      Weather Forecast Condition Type:
                    </label>
                    <Input
                      inputType="select"
                      isMulti
                      isClearable
                      placeholder="Select Weather Forecast Condition Types"
                      value={filters?.weatherForecastFilters?.conditions || []}
                      onChange={(v: string[]) => {
                        if (v.length > 5) {
                          toast.error(
                            "You can select a maximum of 5 conditions."
                          );
                          return;
                        }
                        setFilters((p: any) => ({
                          ...p,
                          weatherForecastFilters: {
                            ...p.weatherForecastFilters,
                            conditions: v,
                          },
                        }));
                      }}
                      options={WeatherForecastOptions}
                      formatOptionLabel={(option: any) => {
                        const Option = WeatherForecastOptions.find(
                          (wac) => wac.value === option.value
                        );
                        const Icon = Option?.icon;
                        return (
                          <div className="d-flex align-items-center">
                            {Icon && <Icon className="me-2" width={20} />}
                            <span>{option.label}</span>
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-auto">
                    <label className="fw-bold text-white p-2">
                      Weather Forecast Slider:
                    </label>
                    <Input
                      inputType="radio"
                      radioName="weatherForecastSlider"
                      value={[!!filters?.weatherForecastFilters?.showSlider]}
                      onChange={([v]: boolean[]) => {
                        setFilters((p: any) => ({
                          ...p,
                          weatherForecastFilters: {
                            ...p.weatherForecastFilters,
                            showSlider: v,
                          },
                        }));
                      }}
                      options={[
                        { value: false, label: "Hide" },
                        { value: true, label: "Show" },
                      ]}
                    />
                  </div>
                  <div className="col-auto">
                    <label className="fw-bold text-white p-2">
                      Map zoom level:
                    </label>
                    <Input
                      inputType="radio"
                      radioName="weatherForecastZoomLevel"
                      value={[filters?.weatherForecastFilters?.mapZoomLevel]}
                      onChange={([v]: number[]) => {
                        setFilters((p: any) => ({
                          ...p,
                          weatherForecastFilters: {
                            ...p.weatherForecastFilters,
                            mapZoomLevel: v,
                          },
                        }));
                      }}
                      options={[
                        { value: 4, label: "Wide" },
                        { value: 6, label: "Country" },
                        { value: 9, label: "Local" },
                      ]}
                    />
                  </div>
                </>
              )}

              {/* {filters.eventsFilters?.isEvent && (
                <>
                  <div className="col-3">
                    <label className="fw-bold text-white p-2">Severity Level:</label>
                    <Input
                      placeholder="Search severity level"
                      options={[
                        { value: "low", label: "Low" },
                        { value: "medium", label: "Medium" },
                        { value: "high", label: "High" },
                        { value: "critical", label: "Critical" },
                      ]}
                      inputType="select"
                      isMulti
                      value={filters.eventsFilters?.severityLevel || []}
                      onChange={(v: string[]) => {
                        setFilters((p: any) => ({
                          ...p,
                          eventsFilters: {
                            ...p.eventsFilters,
                            severityLevel: v,
                          },
                        }));
                      }}
                    />
                  </div>
                  <div className="col-auto">
                    <span className="fw-bold text-white">
                      Filter by Events:
                    </span>
                    <Input
                      inputType="select"
                      isMulti
                      value={filters.EventsUuids || []}
                      onChange={(v: any) =>
                        setFilters({ ...filters, EventsUuids: v })
                      }
                      loadOptions={
                        ((search: string, prevOptions: any) =>
                          loadEventOptionsPaginated(
                            search,
                            prevOptions,
                            filters.EventsUuids
                          )) as any
                      }
                      // getValueLabels={
                      //   (() =>
                      //     loadEventOptionsPaginated(
                      //       "",
                      //       [],
                      //       filters.EventsUuids
                      //     ).then((res) => res.options)) as any
                      // }
                      options={[]}
                    />
                  </div>
                </>
              )} */}
            </div>
          </div>
          {bottom}
        </div>
        {beforeTimeline}
        {/* TIMELINES */}
        {/* {filters.eventsFilters?.isEvent && (
          <div className="mt-3 mb-5 row gap-5">
            <div className="col-auto">
              <span className="fw-bold">
                {" "}
                <TimeLineIcon width={40} className="me-2" />
                Timeline:
              </span>
            </div>
            <div className="col-8">
              <div className="position-relative">
                <div
                  className="position-absolute translate-middle-x text-center fw-bold"
                  style={{
                    top: 20,
                    left: `0%`,
                  }}
                >
                  {" "}
                  SHOW ALL
                </div>
                {Array.from(
                  Array(
                    alertPeriodOptions[filters.eventsFilters.period || "daily"]
                      ?.slices
                  ).keys()
                ).map((a, s) => (
                  <div
                    key={s}
                    className="position-absolute translate-middle-x text-center"
                    style={{
                      top: 25,
                      left: `${((s + 1) /
                        alertPeriodOptions[
                          filters.eventsFilters?.period || "daily"
                        ].slices) *
                        100
                        }%`,
                    }}
                  >
                    {new Date(
                      new Date(
                        new Date().setDate(new Date().getDate() + (s ?? 0))
                      ).setHours(0, 0, 0)
                    ).toLocaleDateString()}
                  </div>
                ))}

                <input
                  type="range"
                  className="w-100"
                  min={-1}
                  max={
                    alertPeriodOptions[filters.eventsFilters.period || "daily"]
                      ?.slices - 1
                  }
                  step={1}
                  value={filters.eventsFilters.range}
                  list="tickmarks"
                  onChange={(e) => {
                    if (e.target.valueAsNumber === -1) {
                      setFilters((p: any) => ({
                        ...p,
                        eventsFilters: {
                          ...p.eventsFilters,
                          StartAt: "",
                          EndAt: "",
                          range: -1,
                        },
                      }));

                      return;
                    }
                    setFilters((p: any) => ({
                      ...p,
                      eventsFilters: {
                        ...p.eventsFilters,
                        range: e.target.valueAsNumber,
                        StartAt:
                          new Date(
                            new Date(
                              new Date().setDate(
                                new Date().getDate() +
                                (e.target.valueAsNumber ?? 0)
                              )
                            ).setHours(0, 0, 0)
                          )
                            .toISOString()
                            .split("T")[0] + " 00:00",
                        EndAt:
                          new Date(
                            new Date(
                              new Date().setDate(
                                new Date().getDate() +
                                (e.target.valueAsNumber ?? 0) +
                                1
                              )
                            ).setHours(0, 0, 0)
                          )
                            .toISOString()
                            .split("T")[0] + " 00:00",
                      },
                    }));
                  }}
                />
                <datalist id="tickmarks">
                  <option>{-1}</option>
                  {Array.from(
                    Array(
                      alertPeriodOptions[
                        filters.eventsFilters.period || "daily"
                      ]?.slices
                    ).keys()
                  ).map((a, s) => (
                    <option key={s}>{s}</option>
                  ))}
                </datalist>
              </div>
            </div>
            <div className="col text-end">
              <CreateEventModal />
            </div>
          </div>
        )} */}
        {filters.weatherFilters?.isWeather && (
          <div className="mt-3 mb-5 row gap-5">
            <div className="col-auto">
              <label className="fw-bold p-2">
                {" "}
                <TimeLineIcon width={40} className="me-2" />
                Timeline:
              </label>
            </div>
            <div className="col-8">
              <div className="position-relative">
                <button
                  className="btn position-absolute translate-middle-x text-center fw-bold"
                  style={{
                    top: 20,
                    left: `0%`,
                  }}
                  onClick={() => {
                    setFilters((p: any) => ({
                      ...p,
                      weatherFilters: {
                        ...p.weatherFilters,
                        StartAt: "",
                        EndAt: "",
                        range: -1,
                      },
                    }));
                  }}
                >
                  {" "}
                  SHOW ALL
                </button>
                {Array.from(
                  Array(
                    alertPeriodOptions[filters.weatherFilters.period || "daily"]
                      ?.slices
                  ).keys()
                ).map((a, s) => (
                  <button
                    key={s}
                    className="btn position-absolute translate-middle-x text-center"
                    style={{
                      top: 25,
                      left: `${
                        ((s + 1) /
                          alertPeriodOptions[
                            filters.weatherFilters?.period || "daily"
                          ].slices) *
                        100
                      }%`,
                    }}
                    onClick={(e) => {
                      const startDate = new Date();
                      setFilters((p: any) => ({
                        ...p,
                        weatherFilters: {
                          ...p.weatherFilters,
                          range: s,
                          StartAt:
                            startDate.getFullYear() +
                            "-" +
                            (startDate.getMonth() + 1) +
                            "-" +
                            (startDate.getDate() + s) +
                            " 00:00",
                          EndAt:
                            startDate.getFullYear() +
                            "-" +
                            (startDate.getMonth() + 1) +
                            "-" +
                            (startDate.getDate() + s + 1) +
                            " 00:00",
                        },
                      }));
                    }}
                  >
                    {new Date(
                      new Date(
                        new Date().setDate(new Date().getDate() + (s ?? 0))
                      ).setHours(0, 0, 0)
                    ).toLocaleDateString()}
                  </button>
                ))}

                <input
                  type="range"
                  className="w-100"
                  min={-1}
                  max={
                    alertPeriodOptions[filters.weatherFilters.period || "daily"]
                      ?.slices - 1
                  }
                  step={1}
                  value={filters.weatherFilters.range}
                  list="tickmarks"
                  onChange={(e) => {
                    if (e.target.valueAsNumber === -1) {
                      setFilters((p: any) => ({
                        ...p,
                        weatherFilters: {
                          ...p.weatherFilters,
                          StartAt: "",
                          EndAt: "",
                          range: -1,
                        },
                      }));

                      return;
                    }

                    const startDate = new Date();
                    setFilters((p: any) => ({
                      ...p,
                      weatherFilters: {
                        ...p.weatherFilters,
                        range: e.target.valueAsNumber,
                        StartAt:
                          startDate.getFullYear() +
                          "-" +
                          (startDate.getMonth() + 1) +
                          "-" +
                          (startDate.getDate() +
                            (e.target.valueAsNumber ?? 0)) +
                          " 00:00",
                        EndAt:
                          startDate.getFullYear() +
                          "-" +
                          (startDate.getMonth() + 1) +
                          "-" +
                          (startDate.getDate() +
                            (e.target.valueAsNumber ?? 0) +
                            1) +
                          " 00:00",
                      },
                    }));
                  }}
                />
                <datalist id="tickmarks">
                  <option>{-1}</option>
                  {Array.from(
                    Array(
                      alertPeriodOptions[
                        filters.weatherFilters.period || "daily"
                      ]?.slices
                    ).keys()
                  ).map((a, s) => (
                    <option key={s}>{s}</option>
                  ))}
                </datalist>
              </div>
            </div>
          </div>
        )}
        {filters.weatherForecastFilters?.isForecast && (
          <div className="mt-3 mb-5 row gap-5">
            <div className="col-auto">
              <span className="fw-bold">
                {" "}
                <TimeLineIcon width={40} className="me-2" />
                Timeline:
              </span>
            </div>
            <div className="col-8 d-flex align-items-center">
              <button
                className="btn border-0"
                disabled={
                  new Date(weatherForecastDates?.[0] || "") < new Date()
                }
                onClick={() => {
                  setFilters((p) => {
                    const startDate = p.weatherForecastFilters?.start
                      ? new Date(p.weatherForecastFilters?.start)
                      : new Date(weatherForecastDates?.[0] || "");
                    startDate.setDate(startDate.getDate() - 1);
                    return {
                      ...p,
                      weatherForecastFilters: {
                        ...(p.weatherForecastFilters || {}),
                        start: startDate.toISOString(),
                        date: "",
                        radius: p.weatherForecastFilters?.radius || 30,
                        steps: p.weatherForecastFilters?.steps || 8,
                        isForecast: true,
                      },
                    };
                  });
                }}
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </button>
              <div className="position-relative flex-grow-1">
                {(weatherForecastDates || []).map((wd, s) => {
                  const d = new Date(wd);

                  const sameDayArray = weatherForecastDates?.filter(
                    (a) =>
                      new Date(a).getDate() === d.getDate() &&
                      new Date(a).getMonth() === d.getMonth() &&
                      new Date(a).getFullYear() === d.getFullYear()
                  );
                  const index = sameDayArray?.indexOf(wd);

                  return (
                    <button
                      key={s}
                      className="btn position-absolute translate-middle-x text-center"
                      style={{
                        top: 25,
                        left: `${
                          (s / ((weatherForecastDates?.length ?? 2) - 1)) * 100
                        }%`,
                      }}
                      onClick={() => {
                        setFilters((p: any) => ({
                          ...p,
                          weatherForecastFilters: {
                            ...p.weatherForecastFilters,
                            date: wd,
                          },
                        }));
                      }}
                    >
                      <div>
                        {`${d.getHours()}`.padStart(2, "0")}:
                        {`${d.getMinutes()}`.padStart(2, "0")}
                      </div>
                      {index === 0 && <div>{d.toLocaleDateString()}</div>}
                    </button>
                  );
                })}

                <input
                  type="range"
                  className="w-100"
                  min={0}
                  max={(weatherForecastDates || [])?.length - 1}
                  step={1}
                  value={weatherForecastDates?.indexOf(
                    filters.weatherForecastFilters.date
                  )}
                  list="tickmarks"
                  onChange={(e) => {
                    setFilters((p: any) => ({
                      ...p,
                      weatherForecastFilters: {
                        ...p.weatherForecastFilters,
                        date: weatherForecastDates?.[e.target.valueAsNumber],
                      },
                    }));
                  }}
                />
                <datalist id="tickmarks">
                  {(weatherForecastDates || []).map((a, s) => (
                    <option key={s}>{s}</option>
                  ))}
                </datalist>
              </div>
              <button
                className="btn"
                onClick={() => {
                  setFilters((p) => {
                    const startDate = p.weatherForecastFilters?.start
                      ? new Date(p.weatherForecastFilters?.start)
                      : new Date(weatherForecastDates?.[0] || "");
                    startDate.setDate(startDate.getDate() + 1);
                    return {
                      ...p,
                      weatherForecastFilters: {
                        ...(p.weatherForecastFilters || {}),
                        start: startDate.toISOString(),
                        date: "",
                        radius: p.weatherForecastFilters?.radius || 30,
                        steps: p.weatherForecastFilters?.steps || 8,
                        isForecast: true,
                      },
                    };
                  });
                }}
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* <JsonCollapse json={filters} /> */}
    </div>
  );
}
